import React from 'react';
import ListViewCardsLoading from './ListViewCardsLoading';
import ListViewDayGroup from './ListViewDayGroup';
import { groupBookingsByDay } from '../../../libs/listViewPresentation';
import type {
  ListViewBooking,
  ListViewColumn,
  ListViewConfiguration,
  ListViewDiff,
  ListViewSetCollapse,
  TimeFormatSetting
} from '../../types';

interface ListViewCardsProps {
  bookings: ListViewBooking[];
  columns: ListViewColumn[];
  /* The bookings are still being fetched; unlike the table, the card view has
     nothing it can lay out without them, so it shows placeholder cards */
  isLoading: boolean;
  configuration: ListViewConfiguration;
  /* Whether each set's member bookings are listed inside its card */
  setCollapse: ListViewSetCollapse;
  statusByID: ListViewDiff['statusByID'];
  now: string;
  dateFormatSetting: string;
  timeFormatSetting: TimeFormatSetting;
}

/* The card view: the bookings in sections per day, with a card per booking and,
   when they are asked for, a set's member bookings inside its card */
const ListViewCards = ({
  bookings,
  columns,
  isLoading,
  configuration,
  setCollapse,
  statusByID,
  now,
  dateFormatSetting,
  timeFormatSetting
}: ListViewCardsProps) => {
  /* The card title column can be configured via card.title, and defaults to the
     first visible column */
  const configuredTitleKey = configuration.card && configuration.card.title;
  const titleColumn =
    columns.find((column) => column.key === configuredTitleKey) || columns[0];
  /* The booking's own start and end are shown as one range in the card's
     header, so they are not detail rows as well. The date they fall on is the
     heading the cards are grouped under. */
  const detailColumns = columns.filter(
    (column) =>
      column !== titleColumn && column.key !== 'start' && column.key !== 'end'
  );
  return (
    <div>
      {isLoading && (
        /* Kept to the columns a card actually shows, and to a handful of rows:
           a card with a dozen detail rows is not what a placeholder should
           promise */
        <ListViewCardsLoading detailRowsCount={Math.min(detailColumns.length, 4)} />
      )}
      {groupBookingsByDay(bookings, configuration).map((group) => (
        <ListViewDayGroup
          key={group.key === null ? 'no-date' : group.key}
          group={group}
          titleColumn={titleColumn}
          detailColumns={detailColumns}
          setCollapse={setCollapse}
          statusByID={statusByID}
          now={now}
          dateFormatSetting={dateFormatSetting}
          timeFormatSetting={timeFormatSetting}
        />
      ))}
    </div>
  );
};

export default ListViewCards;
